/**
 * GIS feature 属性パネル (docs/22 要件4)。
 * 点/線/面を選択すると属性情報を表示する。PC は地図右のサイドパネル、モバイルはボトムシート。
 * (既存に hybrid 先例が無いため新規。Modal/FilterPanel の a11y — Esc / overlay / role=dialog を踏襲。)
 */

import { useEffect } from 'react';
import type { GisFeatureClickInfo } from './MapView';

interface Props {
  info: GisFeatureClickInfo | null;
  onClose: () => void;
}

export function GisFeaturePanel({ info, onClose }: Props) {
  useEffect(() => {
    if (!info) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [info, onClose]);

  if (!info) return null;

  const entries = Object.entries(info.properties).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  );

  return (
    // 外側は pointer-events-none で地図操作を通し、パネル/モバイル backdrop のみ受け取る。
    <div className="pointer-events-none fixed inset-0 z-40">
      {/* モバイルのみ backdrop (タップで閉じる)。PC は地図を触れるよう backdrop なし。 */}
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        onClick={onClose}
        className="pointer-events-auto absolute inset-0 bg-black/30 sm:hidden"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`GIS 属性: ${info.layerName}`}
        className="pointer-events-auto absolute inset-x-0 bottom-0 flex max-h-[60vh] flex-col overflow-hidden rounded-t-xl bg-white shadow-xl sm:inset-y-0 sm:right-0 sm:bottom-auto sm:left-auto sm:max-h-none sm:w-80 sm:rounded-none sm:border-l sm:border-slate-200 sm:shadow-lg"
      >
        <header className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <p className="text-xs text-slate-500">GIS レイヤー</p>
            <h2 className="truncate text-sm font-bold text-slate-900">{info.layerName}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            autoFocus
            className="shrink-0 rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            ✕
          </button>
        </header>
        <div className="flex-1 overflow-auto px-4 py-3">
          {entries.length === 0 ? (
            <p className="text-sm text-slate-500">属性情報はありません。</p>
          ) : (
            <dl className="grid grid-cols-1 gap-y-2">
              {entries.map(([key, value]) => (
                <div key={key} className="border-b border-slate-100 pb-2 last:border-0">
                  <dt className="text-xs font-medium text-slate-500">{key}</dt>
                  <dd className="mt-0.5 break-words text-sm text-slate-800">{String(value)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </aside>
    </div>
  );
}
